import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useThemeStore } from '@/store/theme'
import { useChannelStore } from '@/store/channel'
import { getAvailableSwitcherLanguages, toUrlLang } from '@/i18n/languages'
import { localizedPath, stripLangPrefix } from '@/utils/localizedPath'
import { toAbsoluteUrl } from '@/utils/seo'

/**
 * Per-route document head.
 *
 * Written imperatively rather than with a head library (or React 19's
 * `<title>`/`<meta>` hoisting) because the hoisting path does not dedupe
 * against the static fallback tags already in `index.html` — crawlers that
 * don't run JS need those, and scrapers that do must not then see two
 * `meta[name=description]`.
 *
 * Every managed tag is rewritten on every call, falling back to the tenant
 * defaults when a page supplies nothing. That makes the head a pure function of
 * the current route, so stale values can't survive a navigation and no
 * unmount-restore bookkeeping is needed.
 */

const JSON_LD_ELEMENT_ID = 'seo-json-ld'

/**
 * The tags shipped in index.html, captured before anything overwrites them.
 * These are the no-JS fallbacks, and double as this hook's defaults.
 */
const STATIC_DEFAULTS = {
  description: readInitialContent('meta[name="description"]') ?? '',
  image: readInitialContent('meta[property="og:image"]') ?? '/og-image.jpg',
}

function readInitialContent(selector: string): string | null {
  if (typeof document === 'undefined') return null
  return document.querySelector(selector)?.getAttribute('content') ?? null
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attribute}="${key}"]`
  let element = document.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function removeMeta(attribute: 'name' | 'property', key: string): void {
  document.querySelector(`meta[${attribute}="${key}"]`)?.remove()
}

function upsertLink(rel: string, href: string): void {
  let element = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    document.head.appendChild(element)
  }
  element.href = href
}

/** Replaces every `link[rel=alternate][hreflang]` with `alternates` (hreflang → absolute URL). */
function syncHreflangLinks(alternates: [string, string][]): void {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove())
  for (const [hreflang, href] of alternates) {
    const element = document.createElement('link')
    element.rel = 'alternate'
    element.hreflang = hreflang
    element.href = href
    document.head.appendChild(element)
  }
}

function upsertJsonLd(data: object | null | undefined): void {
  const existing = document.getElementById(JSON_LD_ELEMENT_ID)
  if (!data) {
    existing?.remove()
    return
  }

  const element = existing ?? document.createElement('script')
  if (!existing) {
    element.id = JSON_LD_ELEMENT_ID
    element.setAttribute('type', 'application/ld+json')
    document.head.appendChild(element)
  }
  element.textContent = JSON.stringify(data)
}

export interface SeoOptions {
  /** Page title, without the store-name suffix. Omit to show the store name alone. */
  title?: string | null
  /** Plain text, ideally <= 160 chars. Run CMS HTML through `toMetaDescription` first. */
  description?: string | null
  /** Share image. Relative paths are resolved against the current origin. */
  image?: string | null
  /**
   * Canonical path, with or without the language prefix (it is added when
   * missing). Defaults to the current pathname (query and hash dropped).
   */
  canonicalPath?: string
  /**
   * Unprefixed path per URL language for hreflang alternates, for pages whose
   * slug differs by language (`{ pt: '/products/caneca', en: '/products/mug' }`).
   * Languages left out use the canonical path.
   */
  alternatePaths?: Record<string, string>
  /** og:type — `website` for listings, `article` for news/events, `product` for PDPs. */
  type?: 'website' | 'article' | 'product'
  /** Keeps a page out of the index: cart, checkout, account, search. */
  noIndex?: boolean
  /** Structured data object, serialized into a single application/ld+json script. */
  jsonLd?: object | null
}

export function useSeo({
  title,
  description,
  image,
  canonicalPath,
  alternatePaths,
  type = 'website',
  noIndex = false,
  jsonLd,
}: SeoOptions): void {
  const { pathname } = useLocation()
  const tenantTheme = useThemeStore((store) => store.tenantTheme)
  const availableLanguageCodes = useChannelStore((store) => store.availableLanguageCodes)
  const defaultLanguageCode = useChannelStore((store) => store.defaultLanguageCode)
  const currentLanguageCode = useChannelStore((store) => store.currentLanguageCode)

  const siteName = tenantTheme.name
  const logoUrl = tenantTheme.logoUrl
  const serializedJsonLd = jsonLd ? JSON.stringify(jsonLd) : null
  const serializedAlternatePaths = alternatePaths ? JSON.stringify(alternatePaths) : null

  useEffect(() => {
    const resolvedTitle = title ? `${title} | ${siteName}` : siteName
    const resolvedDescription = description || STATIC_DEFAULTS.description
    // Facebook, X and most other scrapers reject SVG share images, and tenant
    // logos are often .svg — fall back past those to the raster default.
    const rasterLogo = logoUrl && !/\.svgx?(\?|$)/i.test(logoUrl) ? logoUrl : undefined
    const resolvedImage = toAbsoluteUrl(image || rasterLogo || STATIC_DEFAULTS.image)
    const lang = toUrlLang(currentLanguageCode)
    const unprefixedPath = stripLangPrefix(canonicalPath ?? pathname)
    const resolvedUrl = toAbsoluteUrl(localizedPath(unprefixedPath, lang))

    document.documentElement.lang = lang

    document.title = resolvedTitle

    upsertMeta('name', 'description', resolvedDescription)

    upsertMeta('property', 'og:title', resolvedTitle)
    upsertMeta('property', 'og:description', resolvedDescription)
    upsertMeta('property', 'og:type', type)
    upsertMeta('property', 'og:url', resolvedUrl)
    upsertMeta('property', 'og:image', resolvedImage)
    upsertMeta('property', 'og:site_name', siteName)
    upsertMeta('property', 'og:locale', currentLanguageCode?.includes('_') ? currentLanguageCode : lang)

    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', resolvedTitle)
    upsertMeta('name', 'twitter:description', resolvedDescription)
    upsertMeta('name', 'twitter:image', resolvedImage)

    upsertLink('canonical', resolvedUrl)

    if (noIndex) {
      upsertMeta('name', 'robots', 'noindex, nofollow')
    } else {
      removeMeta('name', 'robots')
    }

    // One alternate per language the channel serves, plus x-default on the
    // channel's default language. Not emitted for noindex pages.
    const pathsByLang = serializedAlternatePaths
      ? (JSON.parse(serializedAlternatePaths) as Record<string, string>)
      : {}
    const langs = noIndex
      ? []
      : getAvailableSwitcherLanguages(availableLanguageCodes).map((option) => option.catalogCode as string)
    const hrefFor = (l: string) => toAbsoluteUrl(localizedPath(pathsByLang[l] ?? unprefixedPath, l))
    const alternates: [string, string][] = langs.map((l) => [l, hrefFor(l)])
    if (langs.length > 1) {
      const defaultLang = toUrlLang(defaultLanguageCode)
      alternates.push(['x-default', hrefFor(langs.includes(defaultLang) ? defaultLang : langs[0])])
    }
    syncHreflangLinks(langs.length > 1 ? alternates : [])

    upsertJsonLd(serializedJsonLd ? (JSON.parse(serializedJsonLd) as object) : null)
  }, [
    title,
    description,
    image,
    canonicalPath,
    serializedAlternatePaths,
    pathname,
    currentLanguageCode,
    defaultLanguageCode,
    availableLanguageCodes,
    type,
    noIndex,
    siteName,
    logoUrl,
    serializedJsonLd,
  ])
}

export default useSeo
